"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { sendEvent, uploadMedia } from "@/lib/interview/api";
import { INTERVIEW } from "@/lib/constants";

/**
 * Speaks a short friendly message out loud using the browser's built-in
 * text-to-speech. Used to gently nudge the candidate back to the interview.
 */
function speak(message: string, lang: string) {
  try {
    const synth = window.speechSynthesis;
    if (!synth || !message) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(message);
    u.lang = lang;
    u.rate = 1;
    u.pitch = 1.05;
    u.volume = 1;
    const voice = synth
      .getVoices()
      .find((v) => v.lang?.toLowerCase().startsWith(lang.slice(0, 2).toLowerCase()));
    if (voice) u.voice = voice;
    synth.speak(u);
  } catch {
    /* speech synthesis unavailable */
  }
}

/** Captures a still frame from the video element at the given size/quality. */
async function grabFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  width: number,
  quality: number,
): Promise<Blob | null> {
  if (!video || video.videoWidth === 0) return null;
  const w = width;
  const h = (video.videoHeight / video.videoWidth) * w || (w * 3) / 4;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

/**
 * Detects the candidate leaving the interview tab/window and reports each
 * occurrence as a violation. Also captures a camera frame when they leave.
 */
export function useProctoring(
  token: string,
  active: boolean,
  voice?: { message: string; lang: string },
  videoRef?: React.RefObject<HTMLVideoElement | null>,
) {
  const [violations, setViolations] = useState(0);
  const [flagged, setFlagged] = useState(false);
  const [warning, setWarning] = useState(false);
  const lastSpoke = useRef(0);
  const lastReport = useRef(0);
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  const videoRefHold = useRef(videoRef);
  videoRefHold.current = videoRef;

  // Warm up the voices list (some browsers load them lazily).
  useEffect(() => {
    try {
      window.speechSynthesis?.getVoices();
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    const canvas = document.createElement("canvas");

    function nudge() {
      const v = voiceRef.current;
      if (!v) return;
      const now = Date.now();
      if (now - lastSpoke.current < 3000) return; // avoid overlapping speech
      lastSpoke.current = now;
      speak(v.message, v.lang);
    }

    async function captureLeaveShot() {
      const video = videoRefHold.current?.current;
      if (!video) return;
      const blob = await grabFrame(video, canvas, 480, 0.7);
      if (blob) {
        await uploadMedia(token, "violation_snapshot", blob).catch(() => {});
      }
    }

    async function report(reason: string) {
      const now = Date.now();
      // visibilitychange + blur often fire together — count once.
      if (now - lastReport.current < 800) return;
      lastReport.current = now;

      nudge();
      void captureLeaveShot();
      const res = await sendEvent(token, "violation", { reason });
      if (typeof res.violationCount === "number") setViolations(res.violationCount);
      if (res.flagged) setFlagged(true);
      setWarning(true);
    }

    function onVisibility() {
      if (document.visibilityState === "hidden") report("tab_hidden");
    }
    function onBlur() {
      // Window lost focus (alt-tab, another app). Count as leaving.
      report("window_blur");
    }

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
    };
  }, [token, active]);

  const dismissWarning = useCallback(() => setWarning(false), []);
  return { violations, flagged, warning, dismissWarning };
}

/** Periodically captures a still frame and records it for the interview log. */
export function useSnapshots(
  token: string,
  videoRef: React.RefObject<HTMLVideoElement | null>,
  active: boolean,
) {
  useEffect(() => {
    if (!active) return;
    const canvas = document.createElement("canvas");

    async function snap() {
      const video = videoRef.current;
      if (!video) return;
      const blob = await grabFrame(video, canvas, 320, 0.6);
      if (blob) await uploadMedia(token, "snapshot", blob).catch(() => {});
    }

    const id = setInterval(snap, INTERVIEW.SNAPSHOT_INTERVAL_MS);
    const t = setTimeout(snap, 2000);
    return () => {
      clearInterval(id);
      clearTimeout(t);
    };
  }, [token, videoRef, active]);
}

/**
 * Pushes a near-live frame to the manager frequently. Frames overwrite a single
 * file server-side, giving a low-latency "live" view without storing history.
 */
export function useLiveFrame(
  token: string,
  videoRef: React.RefObject<HTMLVideoElement | null>,
  active: boolean,
) {
  useEffect(() => {
    if (!active) return;
    const canvas = document.createElement("canvas");
    let busy = false;

    async function push() {
      if (busy) return;
      const video = videoRef.current;
      if (!video) return;
      busy = true;
      try {
        const blob = await grabFrame(video, canvas, 480, 0.5);
        if (blob) await uploadMedia(token, "live", blob).catch(() => {});
      } finally {
        busy = false;
      }
    }

    const id = setInterval(push, INTERVIEW.LIVE_FRAME_INTERVAL_MS);
    const t = setTimeout(push, 800);
    return () => {
      clearInterval(id);
      clearTimeout(t);
    };
  }, [token, videoRef, active]);
}
